import React, { useEffect, useState } from 'react';
import { IAnalyticsFilters, IAnalyticsPaymentsResponse } from '@/interfaces/Dashboard/dashboard.interface';
import { AnalyticsExportButton } from './AnalyticsExportButton';
import { CommonCard } from '@/components/common/CommonCard';
import { SectionCard } from '@/components/common/SectionCard';
import { CreditCard, CheckCircle2, XCircle, Clock, RotateCcw, Wallet, Landmark, Smartphone, Banknote, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { CommonCardSkeleton, DonutChartSkeleton } from '@/components/ui/Loaders/DashboardSkeletons';
import { EmptyChart } from '@/components/common/EmptyChart';
import { NoDataFound } from '@/components/common/NoDataFound';
import CommonTable from '@/components/common/CommonTable';
import { formatRand, formatChartDate } from '@/utility/helper';

const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6'];

interface PaymentSectionProps {
    filters: IAnalyticsFilters;
    data: IAnalyticsPaymentsResponse['data'] | null;
    isLoading: boolean;
}


export const PaymentSection: React.FC<PaymentSectionProps> = ({ filters, data, isLoading }) => {
    
    if (isLoading) {
        return (
            <div className="space-y-6 animate-pulse">
                <div className="flex justify-between items-center mb-4">
                    <div className="h-6 w-32 bg-gray-200 rounded"></div>
                    <div className="h-8 w-24 bg-gray-200 rounded"></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    {Array.from({ length: 5 }).map((_, i) => <CommonCardSkeleton key={i} />)}
                </div>
            </div>
        );
    }

    const kpis = data?.kpis || {} as any;
    const breakdown = data?.breakdown || [] as any;
    const charts = data?.charts || {} as any;

    const kpiCards = [
        { label: 'Total Transactions', value: kpis.totalTransactions ?? 0, icon: <CreditCard />, color: '#3B82F6' },
        { label: 'Successful Payments', value: kpis.successful ?? 0, icon: <CheckCircle2 />, color: '#10B981' },
        { label: 'Failed Payments', value: kpis.failed ?? 0, icon: <XCircle />, color: '#EF4444' },
        { label: 'Pending Payments', value: kpis.pending ?? 0, icon: <Clock />, color: '#F59E0B' },
        { label: 'Refunded Payments', value: kpis.refunded ?? 0, icon: <RotateCcw />, color: '#6B7280' },
    ];

    const getIconForMethod = (method: string) => {
        switch ((method || '').toLowerCase()) {
            case 'cod': return <Banknote className="w-5 h-5 text-emerald-500" />;
            case 'card': return <CreditCard className="w-5 h-5 text-blue-500" />;
            case 'upi': return <Smartphone className="w-5 h-5 text-purple-500" />;
            case 'wallet': return <Wallet className="w-5 h-5 text-amber-500" />;
            default: return <Landmark className="w-5 h-5 text-gray-500" />;
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-foreground">Payment Breakdown</h2>
                <AnalyticsExportButton
                    exportUrl="/reports/analytics/export?type=payments"
                    fileName="payment_report.xlsx"
                    filters={filters}
                />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                {kpiCards.map((card, i) => (
                    <CommonCard
                        key={i}
                        label={card.label}
                        value={card.value}
                        icon={card.icon}
                        iconColor={card.color}
                    />
                ))}
            </div>

            {/* Breakdown Tiles */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {breakdown.map((item, idx) => (
                    <div key={idx} className="bg-card border rounded-xl p-4 flex items-center justify-between shadow-sm">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-secondary/10">
                                {getIconForMethod(item.method)}
                            </div>
                            <div>
                                <p className="text-sm font-semibold">{item.method}</p>
                                <p className="text-xs text-muted-foreground">{item.count} txns ({item.percentage}%)</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <p className="font-bold">{formatRand(item.amount)}</p>
                        </div>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                 <SectionCard title="Payment Method Distribution" icon={<PieChart />}>
                    <div className="h-[300px] w-full flex items-center justify-center">
                        {!breakdown || breakdown.length === 0 ? (
                            <EmptyChart height="h-[300px]" />
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={breakdown}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={70}
                                    outerRadius={100}
                                    paddingAngle={5}
                                    dataKey="amount"
                                    nameKey="method"
                                >
                                    {breakdown.map((_, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                    formatter={(value: number) => [formatRand(value), 'Revenue']}
                                />
                                <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px' }}/>
                            </PieChart>
                        </ResponsiveContainer>
                        )}
                    </div>
                </SectionCard>

                <SectionCard title="Payment Success Rate" icon={<TrendingUp />}>
                    <div className="h-[300px] w-full">
                        {!charts.successRate || charts.successRate.length === 0 ? (
                            <EmptyChart height="h-[300px]" />
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={charts.successRate}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }}  tickFormatter={formatChartDate} interval={0} angle={-45} textAnchor="end" height={60} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} />
                                    <Tooltip
                                        cursor={{ fill: '#F3F4F6' }}
                                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                    />
                                    <Bar dataKey="success" name="Successful" stackId="a" fill="#10B981" barSize={32} />
                                    <Bar dataKey="failed" name="Failed" stackId="a" fill="#EF4444" radius={[4, 4, 0, 0]} barSize={32} />
                                    <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px' }}/>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </SectionCard>
            </div>
            
             <SectionCard title="Revenue by Payment Method" icon={<CreditCard />} className="overflow-x-auto">
                <CommonTable 
                    columns={[
                        { key: 'method', header: 'Payment Method', render: (row: any) => (
                            <div className="flex items-center gap-2">
                                {getIconForMethod(row.method)} {row.method}
                            </div>
                        ), className: 'font-medium text-foreground' },
                        { key: 'count', header: 'Transactions', className: 'text-right', headerClassName: 'text-right' },
                        { key: 'percentage', header: 'Percentage', render: (row: any) => `${row.percentage}%`, className: 'text-right text-muted-foreground', headerClassName: 'text-right' },
                        { key: 'amount', header: 'Total Revenue', render: (row: any) => formatRand(row.amount), className: 'text-right font-medium', headerClassName: 'text-right' }
                    ]}
                    data={breakdown || []}
                />
            </SectionCard>
        </div>
    );
};
